import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInBusinessDays as dnfsDifferenceInBusinessDays,
  endOfWeek,
  format,
  formatDistanceStrict,
  formatISO,
  intervalToDuration,
  isFirstDayOfMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subWeeks
} from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import locale from "date-fns/locale/en-US"

import formatDistance from "./dateLocale/formatDistance"

export interface FormatDateAgo {
  date: Date
  dateFormat: Date
  short?: boolean
  sufixAgo?: boolean
}

export interface FormatDateOptions {
  dateOnly?: boolean
  timeZone?: string
}

/** Backend `LocalDate` (`yyyy-MM-dd`): a calendar day, read as UTC midnight. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
/** Trailing `Z` or offset; backend `LocalDateTime` has none but is UTC. */
const ZONE_DESIGNATOR_PATTERN = /[T ].*(Z|[+-]\d{2}(:?\d{2})?)$/i

let activeTimeZoneProvider: () => string | undefined = () => undefined
let browserTimeZone: string | undefined
const timeZoneValidity = new Map<string, boolean>()

/** Sets the profile time zone source; called from `main.ts` to avoid importing the store here. */
export const setActiveTimeZoneProvider = (provider: () => string | undefined) => {
  activeTimeZoneProvider = provider
}

export const getBrowserTimeZone = () => {
  if (!browserTimeZone) browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return browserTimeZone
}

const isValidTimeZone = (timeZone: string) => {
  if (!timeZoneValidity.has(timeZone)) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone })
      timeZoneValidity.set(timeZone, true)
    } catch {
      timeZoneValidity.set(timeZone, false)
    }
  }
  return timeZoneValidity.get(timeZone)
}

/** Profile time zone if valid (legacy values like `UTC+3` are not), else the browser's. */
export const resolveActiveTimeZone = () => {
  const timeZone = activeTimeZoneProvider()
  return timeZone && isValidTimeZone(timeZone) ? timeZone : getBrowserTimeZone()
}

const isValidDate = (date: Date) => !Number.isNaN(date.getTime())

/** Parses a backend date: zone-less values are read as UTC, non-ISO strings fall back to `new Date`. */
export const toDate = (value: Date | string | number): Date => {
  if (typeof value !== "string") return new Date(value)
  if (DATE_ONLY_PATTERN.test(value)) return parseISO(`${value}T00:00:00Z`)
  const parsed = parseISO(ZONE_DESIGNATOR_PATTERN.test(value) ? value : `${value}Z`)
  return isValidDate(parsed) ? parsed : new Date(value)
}

const isQuarterHourAligned = (date: Date) =>
  date.getUTCMinutes() % 15 === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0

/**
 * Calendar day as local midnight, the same for every viewer. A `Date` is kept as is. A quarter-hour
 * aligned instant (UTC midnight or a creator's local midnight from older clients) means the nearest
 * UTC midnight, any other time of day means its UTC day.
 */
export const toCalendarDate = (value: Date | string | number) => {
  if (value instanceof Date) return new Date(value)
  const date = toDate(value)
  if (!isValidDate(date)) return date
  const nextDay = isQuarterHourAligned(date) && date.getUTCHours() >= 12 ? 1 : 0
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + nextDay)
}

/** The instant's day in the active time zone, as local midnight. */
export const toActiveCalendarDate = (value: Date | string | number) => {
  const date = toDate(value)
  if (!isValidDate(date)) return date
  return parseISO(formatInTimeZone(date, resolveActiveTimeZone(), "yyyy-MM-dd"))
}

/** Today in the active time zone, as local midnight. */
export const getTodayCalendarDate = () => toActiveCalendarDate(new Date())

/** Calendar days between two stored instants, each read as the day it stands for on every device. */
export const getCalendarDaysBetween = (from: Date, to: Date) =>
  isValidDate(from) && isValidDate(to)
    ? differenceInCalendarDays(toCalendarDate(to.toISOString()), toCalendarDate(from.toISOString()))
    : NaN

/**
 * Calendar days from the instant's day to today, both in the active time zone. Pass `today` when
 * calling in a loop so it is not recomputed for every item.
 */
export const getDaysAgo = (value: Date | string | number, today = getTodayCalendarDate()) =>
  differenceInCalendarDays(today, toActiveCalendarDate(value))

/** Calendar days from today to the instant's day in the active time zone, never negative. */
export const getDaysUntil = (value: Date | string | number) => Math.max(-getDaysAgo(value), 0)

/** "N day(s)" until the trial ends, counting the last day as "1 day". */
export const getTrialRemainingLabel = (value: Date | string | number) => {
  const days = Math.max(getDaysUntil(value), 1)
  return `${days} day${days === 1 ? "" : "s"}`
}

/** Today as `yyyy-MM-dd` in the active time zone. */
export const getTodayKey = () => formatInTimeZone(new Date(), resolveActiveTimeZone(), "yyyy-MM-dd")

/**
 * Single entry point for user-facing dates; invalid values render as "".
 * Instants use the active time zone, calendar days pass `dateOnly: true` so they never shift.
 */
export const formatDate = (
  date: Date | string | number,
  dateFormat: string,
  options: FormatDateOptions = {}
) => {
  if (options.dateOnly) {
    const calendarDate = toCalendarDate(date)
    return isValidDate(calendarDate) ? format(calendarDate, dateFormat) : ""
  }

  const instant = toDate(date)
  if (!isValidDate(instant)) return ""
  return formatInTimeZone(instant, options.timeZone ?? resolveActiveTimeZone(), dateFormat)
}

export const parseDate = (date?: string, isUTC?: boolean): Date => {
  if (!date) return new Date()
  if (isUTC) return toDate(date)
  return parseISO(date)
}

export const nextMonths = (date: Date, monthsAgo: number) => {
  if (isFirstDayOfMonth(date)) {
    return addMonths(date, monthsAgo)
  } else {
    return addMonths(startOfMonth(date), monthsAgo + 1)
  }
}

export const formatDateAgo = (params: FormatDateAgo) => {
  const { date, dateFormat, short, sufixAgo } = params
  const options = short
    ? {
        addSuffix: false,
        locale: {
          ...locale,
          formatDistance
        }
      }
    : undefined
  let timeAgo = formatDistanceStrict(date, dateFormat, options)
  const plural = ["day", "week", "month", "year", "hour", "minute", "second"]
  const selectedParam = plural.find(param => timeAgo.includes(param))
  if (selectedParam) {
    const count = parseInt(timeAgo)
    timeAgo = `${count} ${count === 1 ? selectedParam : `${selectedParam}s`}`
  }
  return sufixAgo && timeAgo !== "now" ? timeAgo + " ago" : timeAgo
}

export const getListDatesFromList = (startDate: Date, stopDate: Date) => {
  const dateArray: Date[] = []
  let currentDate = startDate
  while (currentDate <= stopDate) {
    dateArray.push(currentDate)
    currentDate = addDays(currentDate, 1)
  }
  return dateArray
}

export const lastWeek = () => {
  const lastWeekDay = subWeeks(getTodayCalendarDate(), 1)

  return {
    start: startOfWeek(lastWeekDay, { weekStartsOn: 1 }),
    end: endOfWeek(lastWeekDay, { weekStartsOn: 1 })
  }
}

export const thisWeek = () => ({
  start: startOfWeek(getTodayCalendarDate(), { weekStartsOn: 1 }),
  end: endOfWeek(getTodayCalendarDate(), { weekStartsOn: 1 })
})

export const last30Days = () => {
  const today = getTodayCalendarDate()

  return {
    start: subDays(today, 29),
    end: today
  }
}

export const differenceInBusinessDays = (end, start) => {
  const newEndDate = new Date(end)
  newEndDate.setDate(newEndDate.getDate() + 1)
  return dnfsDifferenceInBusinessDays(newEndDate, start)
}

/**
 * All-day events (Time Off) are calendar dates rather than instants: a day off on 24 Aug must read
 * as 24 Aug in every time zone. They are persisted pinned to UTC midnight, so the local calendar day
 * is written with `toAllDayISOString` and must be read back with `fromAllDayISOString`.
 * Parsing such a value with a plain `new Date(iso)` resolves it in the viewer's zone instead, which
 * shifts the day backwards for negative UTC offsets (24 Aug renders as 23 Aug in the US).
 */
export const toAllDayISOString = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString()

export const fromAllDayISOString = (value: string | Date) => {
  const date = toDate(value)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export {
  differenceInHours,
  differenceInDays,
  subDays,
  addDays,
  formatISO,
  startOfDay,
  differenceInCalendarDays,
  intervalToDuration
}
